import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import type { Fridge } from "../types";
import { captureApolloError } from "../utils/captureApolloError";
import { fireAndForget } from "../utils/fireAndForget";
import { INITIAL_STATE_QUERY } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";

// When the initial query is sent again after a boot that brought no state, counted from the moment
// the boot query settled: early and often at first, because a backend that was out for a moment is
// the likely cause, then ever more rarely.
export const INITIAL_STATE_RETRY_AT_MS = [
	30_000,
	60_000,
	2 * 60_000,
	5 * 60_000,
	10 * 60_000,
	15 * 60_000,
	30 * 60_000,
	45 * 60_000,
	60 * 60_000,
];
// After the last step: once per hour, for as long as the tablet has no state.
export const INITIAL_STATE_RETRY_EVERY_MS = 60 * 60_000;

type RetryArgs = {
	/** The boot query has settled and no initial state is in: it brought no fridge, or errors only. */
	hasNoInitialState: boolean;
	appConnected: boolean;
	fridgeId: string;
	refetch: () => Promise<{ data?: { fridge?: Fridge | null } }>;
	onData: (fridge: Fridge) => void;
};

/**
 * Asks for the initial state again, on a schedule, after a boot whose query brought none.
 *
 * Such a boot (no fridge in the answer, or errors only) sets no fridge error and takes no connection
 * down, so neither useRecoverFromFridgeError nor useReconnectAfterBrokenSubscription ever sends the
 * query again. The page falls through to an empty menu, the hourly price refetch fills in dishes
 * and the QR code, and the tablet sells without its subscription until something reloads it.
 * Without this hook a backend that was out for the moment of the boot costs the fridge an hour, and
 * then gives it only half of its state.
 *
 * The first answer with a fridge is handed to onData, which sets the initial state; that ends the
 * schedule, opens the startup gate of the subscription, and from there the boot goes on as usual.
 * A fridge that answers with an error (no active sales point, unauthorized) is a state as well: it
 * ends the schedule, and useRecoverFromFridgeError owns it from there.
 *
 * The schedule goes by the clock, not by the attempts: a step is left out while the app is offline
 * (the query could not succeed, and the reconnect sends it itself) and while the answer of the step
 * before is still on its way.
 *
 * Same defensive idioms as the hooks beside it: refetch, onData and the connection are read through
 * refs, so a provider that renders often neither restarts the schedule nor leaves it with stale
 * functions; the timer is cleared when the state is in and on unmount.
 */
export const useRetryInitialStateQuery = ({
	hasNoInitialState,
	appConnected,
	fridgeId,
	refetch,
	onData,
}: RetryArgs): void => {
	const refetchRef = useRef(refetch);
	const onDataRef = useRef(onData);
	const appConnectedRef = useRef(appConnected);

	useEffect(() => {
		refetchRef.current = refetch;
	}, [refetch]);

	useEffect(() => {
		onDataRef.current = onData;
	}, [onData]);

	useEffect(() => {
		appConnectedRef.current = appConnected;
	}, [appConnected]);

	useEffect(() => {
		if (!(hasNoInitialState && fridgeId)) {
			return;
		}
		let disposed = false;
		let inFlight = false;
		let timer: number | undefined;

		const ask = async (): Promise<void> => {
			if (inFlight || !appConnectedRef.current) {
				return;
			}
			inFlight = true;
			try {
				logToSentry(`Sending initial query after a boot without a state on fridge: ${fridgeId}`, "warning");
				const res = await refetchRef.current();
				if (disposed) {
					return;
				}
				if (res.data?.fridge) {
					logToSentry(`Initial state arrived after a boot without one on fridge: ${fridgeId}`, "warning");
					onDataRef.current(res.data.fridge);
					return;
				}
				captureApolloError(
					new Error("Initial query error"),
					{
						kind: "Initial_Query_Error",
						query: INITIAL_STATE_QUERY.kind,
						variables: { fridgeId: fridgeId },
					},
					{
						ResponseData: res,
					},
				);
			} catch (err) {
				Sentry.captureException(err);
			} finally {
				inFlight = false;
			}
		};

		// The next step is armed before the query of this one is sent, so that nothing the query
		// does can end the schedule.
		const arm = (step: number): void => {
			const at = INITIAL_STATE_RETRY_AT_MS[step];
			const wait = at === undefined ? INITIAL_STATE_RETRY_EVERY_MS : at - (INITIAL_STATE_RETRY_AT_MS[step - 1] ?? 0);
			timer = window.setTimeout(() => {
				arm(step + 1);
				fireAndForget(ask());
			}, wait);
		};
		arm(0);

		return () => {
			disposed = true;
			window.clearTimeout(timer);
		};
	}, [hasNoInitialState, fridgeId]);
};
